/* eslint-disable max-lines -- cohesive composer add-menu: the grouped root actions, picker dialogs, and remaining flyouts share trigger/popover state. */

/**
 * "Link a session or patient" button for the Work-home composer.
 *
 * Mirrors the affordance Evidence's chat input exposes via
 * ``ContextLinkingButton`` — but built fresh against composer-local
 * state rather than the shared ``linkedSessionContextAtom`` /
 * ``linkedPatientContextAtom`` atoms. Reasons for not reusing the
 * Evidence atoms:
 *
 *   - Evidence's linked context survives across chats and is part of
 *     the "this chat is about X" semantic. The composer linkage is
 *     per-message context that gets serialised into the first turn
 *     of a brand-new agent chat, then doesn't persist.
 *   - Keeping the state composer-local means the agent-chat surface
 *     doesn't accidentally inherit the linkage when the user lands
 *     there post-create.
 *
 * Picker dialogs list recent sessions and clinic patient profiles supplied by
 * the app-side query delegate. Clicking adds the
 * item to the composer's ``linkedItems`` state; the composer renders
 * dismissable chips above the textarea and serialises the items into
 * a context preamble on submit.
 */

import { Button } from '@heidi/design-system/button';
import { FramedIcon, type FramedIconElement } from '@heidi/design-system/framed-icon';
import { Routines } from '@heidi/design-system/icons';
import { Kbd } from '@heidi/design-system/kbd';
import { Label } from '@heidi/design-system/label';
import { Popover, PopoverContent, PopoverTrigger } from '@heidi/design-system/popover';
import { PopoverMenuItem } from '@heidi/design-system/popover-menu-item';
import { Switch } from '@heidi/design-system/switch';
import { TypographyP2 } from '@heidi/design-system/typography';
import { cn } from '@heidi/utils';
import { AudioLines, Microscope, Paperclip, Plus, UsersRound } from 'lucide-react';
import { useCallback, useMemo, useState, type ReactNode } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import {
  linkedContextItemAddMenuPickType,
  WORK_HOME_COMPOSER_ENTRY,
  type TrackWorkHomeComposerCta,
  type TrackWorkHomeComposerRoutineActioned,
  type TrackWorkHomeComposerViewed,
  type WorkflowSkill,
  type WorkMode,
  type WorkSurface,
} from '../../../contracts';
import {
  linkedContextItemKey,
  type LinkedContextItem,
} from '../../../contracts/projects/linked-context';
import { composerToolbarIconButtonClassName } from '../../../data/projects/components/composer-toolbar-icon-button';
import type { LinkableContextItems } from '../../../data/projects/useLinkableContextItems';
import type { WorkRunSpeed } from '../../composer/ProjectRunOptionsSelector.utils';
import { ContextPickerDialog } from './ContextPickerDialog';
import {
  ADD_MENU_FRAMED_ICON_CLASSNAME,
  ADD_MENU_ROW_CLASSNAME,
  ContextSubmenu,
  ShortcutsFlyout,
  SkillPickerList,
  SourcesSubmenu,
  SpeedSubmenu,
  type SourcesPopoverComponent,
} from './LinkContextButton.parts';

export type { LinkedContextItem };

/** A portaled flyout sits outside this menu. Pointer and focus there are not a dismiss. */
const isNestedPopperTarget = (target: EventTarget | null): boolean =>
  target instanceof Element &&
  target.closest('[data-radix-popper-content-wrapper]') != null;

const messages = defineMessages({
  triggerAriaLabel: {
    defaultMessage: 'Add context or attach files',
    id: '2nAZLv7l1p',
    description: 'Composer add-menu trigger accessible name',
  },
  attachFilesAndImages: {
    defaultMessage: 'Attach files & images',
    id: 'mWFkZGP700',
    description: 'Composer add-menu action to attach photos or files',
  },
  sessions: {
    defaultMessage: 'Sessions',
    id: 'YAzrFo1/zd',
    description: 'Composer add-menu action to attach a session',
  },
  patients: {
    defaultMessage: 'Patients',
    id: 'tlIgCuVhAh',
    description: 'Composer add-menu action to attach a patient',
  },
  evidenceOnly: {
    defaultMessage: 'Evidence only',
    id: 'R+pSFnIHL4',
    description: 'Composer add-menu switch that restricts the prompt to Evidence',
  },
  shortcuts: {
    defaultMessage: 'Shortcuts',
    id: 'smdQY6mGl8',
    description: 'Composer add-menu row that opens the shortcuts flyout',
  },
});

/**
 * Optional routine-invoke surface. When provided, the ``+`` menu grows a
 * searchable "Routines" submenu beneath the patient picker so a recorded
 * routine can be invoked straight from the composer (the richer Customise
 * screen lives in the Discover surface).
 */
interface LinkContextButtonSkillsMenu {
  readonly skills: readonly WorkflowSkill[];
  readonly onInvoke: (skill: WorkflowSkill) => void;
}

/**
 * Optional Fast/Normal inference-speed control. When provided, the ``+``
 * menu grows a Speed submenu. The host owns the flag read and the run-flag
 * state — this leaf only renders the picker.
 */
export interface LinkContextButtonSpeedMenu {
  readonly value: WorkRunSpeed;
  readonly onChange: (next: WorkRunSpeed) => void;
}

export interface LinkContextButtonModeMenu {
  readonly value: WorkMode;
  readonly onChange: (next: WorkMode) => void;
}

/**
 * Optional spotlight over the Evidence-only row. When `open` is true the + menu
 * stays open and `wrap` replaces that row (e.g. with a tour card). The host
 * owns the tour; this leaf only holds the menu open and hands the row through.
 */
export interface LinkContextButtonModeRowSpotlight {
  readonly open: boolean;
  readonly wrap: (row: ReactNode) => ReactNode;
  /** Fired after Evidence-only toggles while the spotlight is holding the menu. */
  readonly onModeRowToggle?: () => void;
}

export interface LinkContextButtonProps {
  /** Currently-linked items rendered as chips on the composer. */
  items: readonly LinkedContextItem[];
  /**
   * Set / replace the linked item. Single-slot semantics — passing
   * a new item drops whatever was previously linked. ``null`` clears.
   * A pick only references the object; nothing opens (AGE-631).
   */
  onSet: (item: LinkedContextItem | null) => void;
  /** When set, a session/patient pick goes here instead of `onSet`. */
  onPickObject?: (item: LinkedContextItem) => void;
  /**
   * Optional file-attach handler. When provided, the popover grows an
   * "Upload → Attach files" section beneath the context pickers, so the
   * single ``+`` entry point on the composer bundles linking context
   * AND attaching files. Clicking it closes the popover and defers to
   * the caller (which opens the hidden ``<input type=file>``).
   */
  onAttachFiles?: () => void;
  /** Optional routine-invoke submenu surfaced below the patient picker. */
  skillsMenu?: LinkContextButtonSkillsMenu;
  /**
   * Optional Fast/Normal speed submenu. Omitted when the host's Flagsmith
   * gate is off, or on a bound project whose run flags are already persisted.
   */
  speedMenu?: LinkContextButtonSpeedMenu;
  /**
   * Host-owned Work/Evidence control. When provided, the menu ends with an
   * Evidence-only switch that writes the same `WorkMode` the chrome pills did.
   */
  modeMenu?: LinkContextButtonModeMenu;
  /**
   * When set and `open`, keeps the + menu open and wraps the Evidence-only row
   * (used by the Heidi II Evidence tour's final step).
   */
  modeRowSpotlight?: LinkContextButtonModeRowSpotlight;
  onAfterPick?: () => void;
  disabled?: boolean;
  /** When set, composer add-menu interactions emit Work home analytics. */
  analyticsSurface?: WorkSurface;
  /**
   * The three emit seams, and REQUIRED rather than optional on purpose: the
   * gate on emitting is `analyticsSurface`, and it stays exactly where it was.
   * An optional emit would add a second, silent gate — a host that forgot one
   * prop would lose the events with nothing failing, which is the failure mode
   * an analytics seam exists to prevent.
   */
  onTrackCta: TrackWorkHomeComposerCta;
  onTrackViewed: TrackWorkHomeComposerViewed;
  onTrackRoutineActioned: TrackWorkHomeComposerRoutineActioned;
  /** The Evidence source-control panel the Sources row opens. See its type. */
  SourcesPopover: SourcesPopoverComponent;
  /**
   * The sessions and patients the two dialogs list, already derived.
   *
   * INJECTED AS THE RESULT AND NOT AS ITS INPUTS, which is a second decision on
   * top of "the sources are app-side". The derivation itself moved to
   * `@heidi/agents/data` and is shared with the inline `@` mention list; what
   * cannot move is where the rows come from, because `useQuerySessionsV2` and
   * `useQueryPatientProfiles` are both REVIEW-CONTEXT branches over app atoms and
   * RBAC — whose list you are looking at — and app-side by a recorded decision on
   * each domain's own carve.
   *
   * Taking the derived items means the app reads those queries in exactly ONE
   * place, the old `useLinkableContextItems` path. Taking the raw sources instead
   * would have made this component's host a second reader of the same queries,
   * which is not just duplication: it puts the read behind a module no existing
   * consumer suite mocks, so three suites that had stubbed the derivation started
   * booting the real workspace stack. Measured, not reasoned about.
   *
   * Filtering by the submenu search stays here — that is picker state.
   */
  pickerItems: LinkableContextItems;
  /**
   * Restricted in-session chats freeze the host session / patient. The + menu
   * keeps upload, sources, and routines, but drops Sessions and Patients.
   */
  hideLinkedContextPickers?: boolean;
  /** Keep Sessions but drop Patients — in-session chats are frozen to one patient. */
  hidePatientPicker?: boolean;
  /**
   * Sources row in `+`. Condensed layout only — collapsed and full use the
   * standalone toolbar control.
   */
  showSourcesInAddMenu?: boolean;
  /**
   * In-session only. The host renders the Evidence shortcut list into this
   * flyout. Omitted on Work home and other chats.
   */
  renderShortcutsFlyout?: (close: () => void) => ReactNode;
}

/**
 * Do not apply the glance-surface cap inside the searchable picker dialogs.
 * The app delegate already supplies only the first loaded sessions page and
 * the patients derived from it — this Infinity means "do not double-cap
 * below that fetched page", not browse-everything.
 *
 * Exported because the host is the one that calls the derivation now, and one
 * declaration of the cap is the point — a host passing a different limit would
 * silently change which rows this menu can show.
 */
export const LINK_CONTEXT_PICKER_LIMIT = Number.POSITIVE_INFINITY;

export const LinkContextButton = ({
  items,
  onSet,
  onPickObject,
  onAttachFiles,
  skillsMenu,
  speedMenu,
  modeMenu,
  modeRowSpotlight,
  onAfterPick,
  disabled = false,
  analyticsSurface,
  onTrackCta,
  onTrackViewed,
  onTrackRoutineActioned,
  SourcesPopover,
  pickerItems,
  hideLinkedContextPickers = false,
  hidePatientPicker = false,
  showSourcesInAddMenu = false,
  renderShortcutsFlyout,
}: LinkContextButtonProps) => {
  const intl = useIntl();
  const [open, setOpen] = useState(false);
  // The DS Switch thumb grows when any ancestor `.group` carries
  // `data-hover-active`, so the row publishes it on hover for a row-wide grow.
  const [modeRowHover, setModeRowHover] = useState(false);
  const [pickerDialog, setPickerDialog] = useState<'sessions' | 'patients' | null>(null);
  const [sessionSearch, setSessionSearch] = useState('');
  const [patientSearch, setPatientSearch] = useState('');
  const [skillSearch, setSkillSearch] = useState('');
  // Exactly one flyout at a time: hovering a sibling row swaps the open
  // submenu instantly, while a submenu's own (grace-delayed) close can only
  // clear itself — never a newer sibling that has since taken over.
  const [openSubmenu, setOpenSubmenu] = useState<
    'sources' | 'skills' | 'speed' | 'shortcuts' | null
  >(null);
  const submenuOpenChange = (
    id: 'sources' | 'skills' | 'speed' | 'shortcuts',
    next: boolean
  ) => {
    setOpenSubmenu((current) => (next ? id : current === id ? null : current));
  };
  const closeAddMenu = useCallback(() => {
    setOpen(false);
    setOpenSubmenu(null);
  }, []);

  // Recent sessions + patients, derived (and de-duped) in the shared hook so this
  // picker and the inline ``@`` mention autocomplete read from one source of
  // truth. The host calls it — see `pickerItems`.
  const { sessionItems: sessionPickerItems, patientItems: patientPickerItems } =
    pickerItems;

  // Mark the currently-linked item so the picker can render an
  // "Active" pill on its row without disabling the click. Picking
  // any row — including the active one — replaces the linkage
  // (single-slot model); the user can also re-click the active row
  // to confirm without surprise.
  const linkedKeys = useMemo(() => new Set(items.map(linkedContextItemKey)), [items]);

  const visibleSessionItems = useMemo(
    () => filterItems(sessionPickerItems, sessionSearch),
    [sessionPickerItems, sessionSearch]
  );
  const visiblePatientItems = useMemo(
    () => filterItems(patientPickerItems, patientSearch),
    [patientPickerItems, patientSearch]
  );
  const visibleSkills = useMemo(() => {
    const all = skillsMenu?.skills ?? [];
    const query = skillSearch.trim().toLowerCase();
    if (!query) return all;
    return all.filter((skill) =>
      [skill.title, skill.slug].some((value) => value.toLowerCase().includes(query))
    );
  }, [skillsMenu?.skills, skillSearch]);

  const closePicker = () => {
    setPickerDialog(null);
    setSessionSearch('');
    setPatientSearch('');
  };

  const handlePick = (item: LinkedContextItem) => {
    if (
      analyticsSurface &&
      (item.type === 'session' ||
        item.type === 'patient' ||
        item.type === 'library_document')
    ) {
      onTrackCta(
        WORK_HOME_COMPOSER_ENTRY.addMenuPick(linkedContextItemAddMenuPickType(item.type)),
        { surface: analyticsSurface }
      );
    }
    if (onPickObject) onPickObject(item);
    else onSet(item);
    // Controlled `open` flips do not fire Dialog onOpenChange, so reset here.
    closePicker();
    setOpenSubmenu(null);
    setOpen(false);
    onAfterPick?.();
  };

  const handleOpenPicker = (type: 'sessions' | 'patients') => {
    if (analyticsSurface) {
      onTrackViewed(
        WORK_HOME_COMPOSER_ENTRY.addMenuSubmenuOpen(
          type === 'sessions' ? 'recent_sessions' : 'patient'
        ),
        { surface: analyticsSurface }
      );
    }
    setOpenSubmenu(null);
    setOpen(false);
    setPickerDialog(type);
  };

  const handleInvokeSkill = (skill: WorkflowSkill) => {
    if (analyticsSurface) {
      onTrackCta(WORK_HOME_COMPOSER_ENTRY.addMenuPick('routine'), {
        surface: analyticsSurface,
      });
      onTrackRoutineActioned({
        surface: analyticsSurface,
        workflow_id: skill.recordingId ?? skill.skillId,
        trigger_source: 'work_composer_add_menu',
      });
    }
    skillsMenu?.onInvoke(skill);
    setOpenSubmenu(null);
    setOpen(false);
  };

  const handleSpeedChange = (next: WorkRunSpeed) => {
    if (!speedMenu || next === speedMenu.value) return;
    if (analyticsSurface) {
      onTrackCta(WORK_HOME_COMPOSER_ENTRY.speed(next), { surface: analyticsSurface });
    }
    speedMenu.onChange(next);
  };

  const handleEvidenceOnlyChange = (checked: boolean) => {
    if (!modeMenu) return;
    const next: WorkMode = checked ? 'evidence' : 'work';
    if (next === modeMenu.value) return;
    if (analyticsSurface) {
      onTrackCta(WORK_HOME_COMPOSER_ENTRY.modeToggle, {
        surface: analyticsSurface,
        work_mode: next,
      });
    }
    modeMenu.onChange(next);
    // Tour step 3: keep the menu up after the toggle, then let the host record
    // engage (closing the tour). Without setOpen(true) the hold ends when the
    // spotlight unmounts and the menu would snap shut on the same click.
    if (modeRowSpotlight?.open === true) {
      setOpen(true);
      modeRowSpotlight.onModeRowToggle?.();
    }
  };

  const hideRoutines = modeMenu?.value === 'evidence';

  // A Label, not a PopoverMenuItem: the Switch is a <button>, so a
  // PopoverMenuItem (also a <button>) would nest interactives and strip
  // the Switch's name. `htmlFor` names it and toggles it row-wide.
  const evidenceOnlyRow = modeMenu ? (
    <Label
      htmlFor="work-home-mode-evidence"
      className={cn(
        'group flex w-full items-center gap-2.5 rounded-md p-2',
        'hover:bg-surface-hover'
      )}
      data-testid="work-home-mode-pill"
      data-hover-active={(!disabled && modeRowHover) || undefined}
      onMouseEnter={() => {
        setOpenSubmenu(null);
        setModeRowHover(true);
      }}
      onMouseLeave={() => setModeRowHover(false)}
    >
      <FramedIcon icon={<Microscope aria-hidden />} size="sm" color="blue" />
      <TypographyP2 className="min-w-0 flex-1 text-text-primary">
        {intl.formatMessage(messages.evidenceOnly)}
      </TypographyP2>
      <Switch
        id="work-home-mode-evidence"
        size="sm"
        checked={modeMenu.value === 'evidence'}
        disabled={disabled}
        onCheckedChange={handleEvidenceOnlyChange}
        data-testid="work-home-mode-evidence"
      />
    </Label>
  ) : null;

  // No row, nothing to spotlight — holding would pin the menu with no way out.
  const spotlightHoldsMenuOpen =
    modeRowSpotlight?.open === true && evidenceOnlyRow !== null;
  const menuOpen = open || spotlightHoldsMenuOpen;

  // Single ``+`` entry point on the composer bar (matches the resting
  // design): one glyph that opens everything the user can add to a
  // prompt — recent sessions / patients to link as context, an
  // optional skill to invoke, plus a file-attach action. Linked items
  // surface as dismissable chips on the composer, so the trigger
  // itself stays a clean icon.
  return (
    <>
      <Popover
        open={menuOpen}
        onOpenChange={(next) => {
          setOpen(next);
          if (next) {
            if (analyticsSurface) {
              onTrackViewed(WORK_HOME_COMPOSER_ENTRY.addMenuOpen, {
                surface: analyticsSurface,
              });
            }
          }
          if (!next) {
            setOpenSubmenu(null);
            setSkillSearch('');
          }
        }}
      >
        {/* No tooltip on the ``+`` — the menu it opens names every option, and a
          hover label on the composer's own toolbar reads as clutter. The
          aria-label carries the name for screen readers. */}
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            size="md"
            iconOnly
            aria-label={intl.formatMessage(messages.triggerAriaLabel)}
            disabled={disabled}
            onPointerDown={(event) => {
              // Keep TipTap focused so compact engagement does not collapse
              // (and remount this control) before the add menu opens — Safari
              // blurs contenteditable before the button receives focus.
              event.preventDefault();
            }}
            className={cn(
              'shrink-0',
              composerToolbarIconButtonClassName,
              'data-[state=open]:bg-surface-hover'
            )}
          >
            <Plus aria-hidden />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          align="start"
          side="bottom"
          sideOffset={8}
          // Marker the hover-driven Sources flyout uses to tell "this add-menu"
          // apart from its own (right-side) flyouts when deciding to auto-close.
          data-add-menu
          className="w-64 p-2"
          onOpenAutoFocus={(e) => e.preventDefault()}
          onFocusOutside={(event) => {
            if (isNestedPopperTarget(event.target)) event.preventDefault();
          }}
          onInteractOutside={(event) => {
            if (isNestedPopperTarget(event.target)) event.preventDefault();
          }}
        >
          <div className="flex min-w-0 flex-col gap-1">
            {onAttachFiles ? (
              <AddMenuAction
                icon={<Paperclip aria-hidden />}
                label={intl.formatMessage(messages.attachFilesAndImages)}
                onMouseEnter={() => setOpenSubmenu(null)}
                onClick={() => {
                  if (analyticsSurface) {
                    onTrackCta(WORK_HOME_COMPOSER_ENTRY.addMenuPick('file'), {
                      surface: analyticsSurface,
                    });
                  }
                  onAttachFiles();
                  setOpen(false);
                }}
              />
            ) : null}
            {hideLinkedContextPickers ? null : (
              <>
                {hidePatientPicker ? null : (
                  <AddMenuAction
                    icon={<UsersRound aria-hidden />}
                    label={intl.formatMessage(messages.patients)}
                    shortcutHint="@"
                    onMouseEnter={() => setOpenSubmenu(null)}
                    onClick={() => handleOpenPicker('patients')}
                  />
                )}
                <AddMenuAction
                  icon={<AudioLines aria-hidden />}
                  label={intl.formatMessage(messages.sessions)}
                  shortcutHint="@"
                  onMouseEnter={() => setOpenSubmenu(null)}
                  onClick={() => handleOpenPicker('sessions')}
                />
              </>
            )}

            {renderShortcutsFlyout ? (
              <ShortcutsFlyout
                open={openSubmenu === 'shortcuts'}
                onOpenChange={(next) => submenuOpenChange('shortcuts', next)}
                label={intl.formatMessage(messages.shortcuts)}
              >
                {openSubmenu === 'shortcuts' ? renderShortcutsFlyout(closeAddMenu) : null}
              </ShortcutsFlyout>
            ) : null}
            {hideRoutines || !skillsMenu || skillsMenu.skills.length === 0 ? null : (
              <ContextSubmenu
                icon={<Routines aria-hidden />}
                label={intl.formatMessage({
                  defaultMessage: 'Routines',
                  id: '/5T7UYjJp3',
                  description:
                    'Routines row in the composer add menu that opens the flyout',
                })}
                shortcutHint="/"
                isActive={false}
                searchPlaceholder={intl.formatMessage({
                  defaultMessage: 'Search routines',
                  id: 'd2vPw4s1Wx',
                  description: 'Placeholder in the routine picker search field',
                })}
                emptyMessage={intl.formatMessage({
                  defaultMessage: 'No matching routines',
                  id: '2PUu6tgyEf',
                  description: 'Empty state when a routine search returns nothing',
                })}
                isEmpty={visibleSkills.length === 0}
                open={openSubmenu === 'skills'}
                onOpenChange={(next) => {
                  submenuOpenChange('skills', next);
                  if (next && analyticsSurface) {
                    onTrackViewed(
                      WORK_HOME_COMPOSER_ENTRY.addMenuSubmenuOpen('routine'),
                      {
                        surface: analyticsSurface,
                      }
                    );
                  }
                }}
                search={skillSearch}
                onSearchChange={setSkillSearch}
              >
                <SkillPickerList skills={visibleSkills} onInvoke={handleInvokeSkill} />
              </ContextSubmenu>
            )}
            {showSourcesInAddMenu || openSubmenu === 'sources' ? (
              <SourcesSubmenu
                SourcesPopover={SourcesPopover}
                open={openSubmenu === 'sources'}
                onOpenChange={(next) => {
                  submenuOpenChange('sources', next);
                  if (next && analyticsSurface) {
                    onTrackViewed(
                      WORK_HOME_COMPOSER_ENTRY.addMenuSubmenuOpen('sources'),
                      {
                        surface: analyticsSurface,
                      }
                    );
                  }
                }}
              />
            ) : null}
            {speedMenu ? (
              <>
                <SpeedSubmenu
                  value={speedMenu.value}
                  onChange={handleSpeedChange}
                  open={openSubmenu === 'speed'}
                  onOpenChange={(next) => {
                    submenuOpenChange('speed', next);
                    if (next && analyticsSurface) {
                      onTrackViewed(
                        WORK_HOME_COMPOSER_ENTRY.addMenuSubmenuOpen('speed'),
                        {
                          surface: analyticsSurface,
                        }
                      );
                    }
                  }}
                />
              </>
            ) : null}
            {spotlightHoldsMenuOpen
              ? modeRowSpotlight.wrap(evidenceOnlyRow)
              : evidenceOnlyRow}
          </div>
        </PopoverContent>
      </Popover>

      <ContextPickerDialog
        type="sessions"
        open={pickerDialog === 'sessions'}
        items={visibleSessionItems}
        linkedKeys={linkedKeys}
        query={sessionSearch}
        onQueryChange={setSessionSearch}
        onPick={handlePick}
        onOpenChange={(next) => {
          if (!next) closePicker();
        }}
      />
      <ContextPickerDialog
        type="patients"
        open={pickerDialog === 'patients'}
        items={visiblePatientItems}
        linkedKeys={linkedKeys}
        query={patientSearch}
        onQueryChange={setPatientSearch}
        onPick={handlePick}
        onOpenChange={(next) => {
          if (!next) closePicker();
        }}
      />
    </>
  );
};

const AddMenuAction = ({
  icon,
  label,
  shortcutHint,
  onClick,
  onMouseEnter,
}: {
  icon: FramedIconElement;
  label: string;
  shortcutHint?: string;
  onClick: () => void;
  onMouseEnter: () => void;
}): JSX.Element => (
  <PopoverMenuItem
    className={cn(ADD_MENU_ROW_CLASSNAME, 'items-center')}
    onClick={onClick}
    onMouseEnter={onMouseEnter}
    icon={<FramedIcon icon={icon} size="sm" className={ADD_MENU_FRAMED_ICON_CLASSNAME} />}
    accessoryEnd={
      shortcutHint ? (
        <Kbd size="compact" aria-hidden>
          {shortcutHint}
        </Kbd>
      ) : undefined
    }
  >
    {label}
  </PopoverMenuItem>
);

const filterItems = (
  items: readonly LinkedContextItem[],
  rawQuery: string
): LinkedContextItem[] => {
  const query = rawQuery.trim().toLowerCase();
  if (!query) return [...items];

  return items.filter((item) => {
    if (item.type === 'session') {
      return [item.sessionName, item.patientName].some((value) =>
        value?.toLowerCase().includes(query)
      );
    }
    if (item.type === 'library_document') {
      return item.filename.toLowerCase().includes(query);
    }
    if (item.type === 'task') {
      return [item.label, item.patientName].some((value) =>
        value?.toLowerCase().includes(query)
      );
    }
    if (item.type === 'template' || item.type === 'collection') {
      return item.label.toLowerCase().includes(query);
    }
    return item.patientName.toLowerCase().includes(query);
  });
};
